import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { AccountService, StorefrontCustomInquiryData } from '../../../api';
import { useLanguage } from '../../../utils/GeneralUtils';

interface CustomInquirySectionProps {
  customInquiry?: StorefrontCustomInquiryData;
}

export const CustomInquirySection = ({ customInquiry }: CustomInquirySectionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const language = useLanguage();
  const { accountId, storefrontId } = useParams();

  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  if (!customInquiry) {
    return null;
  }

  const title = customInquiry.title?.[language];
  const subtitle = customInquiry.subtitle?.[language];
  const buttonLabel = customInquiry.button_label?.[language] || t('storefront.customInquirySubmit');

  const handleClose = () => {
    setOpen(false);
    if (submitted) {
      setSubmitted(false);
      setError(false);
    }
  };

  return (
    <Grid container flexDirection="column" alignItems="center" mb={isSmallScreen ? 6 : 10}>
      <Grid container flexDirection="column" alignItems="center" sx={{ maxWidth: 800, margin: '0 auto' }}>
        {title && (
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              marginBottom: subtitle ? 1 : isSmallScreen ? 3 : 4,
              textAlign: 'center',
            }}>
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              textAlign: 'center',
              marginBottom: isSmallScreen ? 3 : 4,
            }}
            dangerouslySetInnerHTML={{ __html: subtitle }}
          />
        )}
        <Button variant="outlined" size="large" onClick={() => setOpen(true)} sx={{ minWidth: 200 }}>
          {buttonLabel}
        </Button>
      </Grid>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isSmallScreen}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {title || t('storefront.customInquiry')}
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {submitted ? (
            <Grid container justifyContent="center" py={4}>
              <Typography variant="h6" sx={{ textAlign: 'center' }}>
                {t('storefront.customInquirySuccess')}
              </Typography>
            </Grid>
          ) : (
            <Formik
              initialValues={{
                name: '',
                email: '',
                phone: '',
                date: '',
                participants: '',
                message: '',
              }}
              onSubmit={async (values) => {
                if (!accountId || !storefrontId) return;
                setError(false);
                try {
                  await AccountService.submitCustomInquiry({
                    account_id: accountId,
                    storefront_id: storefrontId,
                    name: values.name || undefined,
                    email: values.email || undefined,
                    phone: values.phone || undefined,
                    date: values.date || undefined,
                    participants: values.participants ? Number(values.participants) : undefined,
                    message: values.message || undefined,
                  });
                  setSubmitted(true);
                } catch {
                  setError(true);
                }
              }}>
              {({ values, handleChange, handleSubmit, isSubmitting }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2} mt={0.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="name"
                        label={t('storefront.customInquiryName')}
                        value={values.name}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="email"
                        label={t('storefront.customInquiryEmail')}
                        type="email"
                        value={values.email}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="phone"
                        label={t('storefront.customInquiryPhone')}
                        type="tel"
                        value={values.phone}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="date"
                        label={t('storefront.customInquiryDate')}
                        value={values.date}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        name="participants"
                        label={t('storefront.customInquiryParticipants')}
                        type="number"
                        value={values.participants}
                        onChange={handleChange}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="message"
                        label={t('storefront.customInquiryMessage')}
                        multiline
                        rows={4}
                        value={values.message}
                        onChange={handleChange}
                      />
                    </Grid>
                    {error && (
                      <Grid item xs={12}>
                        <Typography color="error" variant="body2" sx={{ textAlign: 'center' }}>
                          {t('storefront.customInquiryError')}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} container justifyContent="center" mt={1} mb={1}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={isSubmitting}
                        sx={{ minWidth: 200 }}>
                        {isSubmitting ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          t('storefront.customInquirySubmit')
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          )}
        </DialogContent>
      </Dialog>
    </Grid>
  );
};
